/**
 * ZIP Utilities
 *
 * Extracts files from a .zip archive, returning a flat File[] of all contained
 * entries (excluding macOS metadata). Intended to expand zip uploads before
 * the gltf/resource matching pipeline runs.
 */

import { unzip } from 'fflate';

const MAX_ENTRY_SIZE_MB = 30;

/**
 * Decompresses a .zip File and returns its contents as a flat File[].
 * Entries larger than MAX_ENTRY_SIZE_MB are skipped.
 * Directory entries (paths ending in '/') are skipped.
 * macOS __MACOSX metadata entries are skipped.
 */
export async function extractZipToFiles(zip: File): Promise<File[]> {
  const maxSize = MAX_ENTRY_SIZE_MB * 1_000_000;
  const buffer = new Uint8Array(await zip.arrayBuffer());

  const zipData = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
    unzip(buffer, { filter: (f) => f.originalSize <= maxSize }, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });

  return Object.entries(zipData)
    .filter(([path]) => !path.startsWith('__MACOSX') && !path.endsWith('/'))
    .map(([path, bytes]) => {
      const name = path.split('/').pop()!;
      return new File([bytes.slice()], name);
    });
}
